/**
 * Deadlines Widget
 *
 * Compact dashboard card showing next 5 upcoming deadlines.
 * Receives services from parent Dashboard's getMyServices() call.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import {
  getDeadlinesFromServices,
  type CalendarDeadline,
} from '../../services/calendarService';
import { getUrgencyDotClass, getUrgencyBadgeClasses, getUrgencyLabel } from '../../utils/urgencyStyles';
import { CalendarIcon } from '../shared/icons';

import type { ServiceSummary } from '../../services/servicesService';

interface DeadlinesWidgetProps {
  services: ServiceSummary[];
}

/**
 *
 */
export function DeadlinesWidget({ services }: DeadlinesWidgetProps) {
  const deadlines = useMemo(
    () => getDeadlinesFromServices(services).slice(0, 5),
    [services],
  );

  return (
    <div className="rounded-lg border border-brand-neutral-200 bg-surface p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-brand-neutral-900">Upcoming Deadlines</h2>
        <Link
          to="/calendar"
          className="text-sm font-medium text-brand-primary-600 hover:text-brand-primary-700"
        >
          View Calendar →
        </Link>
      </div>

      <div className="mt-4 space-y-3">
        {deadlines.length === 0 ? (
          <div className="py-4 text-center">
            <CalendarIcon className="mx-auto h-8 w-8 text-brand-neutral-300" />
            <p className="mt-2 text-sm text-brand-neutral-500">No upcoming deadlines</p>
          </div>
        ) : (
          deadlines.map((d) => (
            <DeadlineRow key={d.id} deadline={d} />
          ))
        )}
      </div>
    </div>
  );
}

function DeadlineRow({ deadline }: { deadline: CalendarDeadline }) {
  const dateFormatted = new Date(deadline.date + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="flex items-center gap-3">
      <span className={`h-2 w-2 shrink-0 rounded-full ${getUrgencyDotClass(deadline.urgency)}`} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-brand-neutral-900">
          {deadline.title}
        </p>
        <p className="truncate text-xs text-brand-neutral-500">
          {deadline.entityName} &middot; {dateFormatted}
        </p>
      </div>
      <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${getUrgencyBadgeClasses(deadline.urgency)}`}>
        {getUrgencyLabel(deadline)}
      </span>
    </div>
  );
}
